import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Badge } from '../../components/ui/badge';
import { 
  Calculator, Save, Loader2, Percent, DollarSign, 
  AlertTriangle, CheckCircle, Info, Settings
} from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const PricingSettingsSection = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);

  useEffect(() => {
    fetchSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/pricing-settings');
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setHasAccess(true);
      } else if (response.status === 403) {
        setHasAccess(false);
      }
    } catch (error) {
      console.error('Erreur chargement pricing settings:', error);
      if (error.message?.includes('403')) {
        setHasAccess(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.put('/admin/pricing-settings', settings);
      if (response.ok) {
        toast.success('Paramètres de pricing sauvegardés');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-tp-text-light" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <Card className="p-8 text-center border-tp-border">
        <AlertTriangle className="h-12 w-12 text-tp-warning mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-tp-text mb-2">Accès restreint</h3>
        <p className="text-tp-text-light">
          Les paramètres de pricing sont réservés aux super administrateurs.
        </p>
      </Card>
    );
  }

  if (!settings) return null;

  return (
    <div className="space-y-6" data-testid="pricing-settings-section">
      {/* Info Box */}
      <div className="p-4 bg-tp-primary/5 rounded-lg border border-tp-primary/20">
        <div className="flex gap-3">
          <Info className="h-5 w-5 text-tp-primary shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-tp-text">
              Ces paramètres sont utilisés pour calculer automatiquement le <strong>coût total employeur</strong>, 
              le <strong>prix de vente recommandé</strong>, et les <strong>marges</strong> de chaque candidat.
            </p>
            <p className="text-xs text-tp-text-light mt-1">
              Modifiable uniquement par les super administrateurs.
            </p>
          </div>
        </div>
      </div>

      {/* Charges et coûts */}
      <Card className="p-6 border-tp-border">
        <h3 className="text-lg font-semibold text-tp-text mb-4 flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-tp-accent" />
          Charges et coûts (% du salaire brut)
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="charges_patronales">Charges patronales (%)</Label>
            <Input
              id="charges_patronales"
              type="number"
              step="0.1"
              value={settings.charges_patronales_percent || ''}
              onChange={(e) => updateSetting('charges_patronales_percent', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="formation">Formation (%)</Label>
            <Input
              id="formation"
              type="number"
              step="0.1"
              value={settings.formation_percent || ''}
              onChange={(e) => updateSetting('formation_percent', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="materiel">Matériel (%)</Label>
            <Input
              id="materiel"
              type="number"
              step="0.1"
              value={settings.materiel_percent || ''}
              onChange={(e) => updateSetting('materiel_percent', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="loyer">Loyer (%)</Label>
            <Input
              id="loyer"
              type="number"
              step="0.1"
              value={settings.loyer_percent || ''}
              onChange={(e) => updateSetting('loyer_percent', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="internet">Internet (%)</Label>
            <Input
              id="internet"
              type="number"
              step="0.1"
              value={settings.internet_percent || ''}
              onChange={(e) => updateSetting('internet_percent', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="management">Management (%)</Label>
            <Input
              id="management"
              type="number"
              step="0.1"
              value={settings.management_percent || ''}
              onChange={(e) => updateSetting('management_percent', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="other">Autres frais généraux (%)</Label>
            <Input
              id="other"
              type="number"
              step="0.1"
              value={settings.other_overheads_percent || ''}
              onChange={(e) => updateSetting('other_overheads_percent', e.target.value)}
              className="mt-1"
            />
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-tp-bg rounded-lg">
          <p className="text-sm text-tp-text-light">
            <strong>Total overhead :</strong>{' '}
            {(
              (settings.charges_patronales_percent || 0) +
              (settings.formation_percent || 0) +
              (settings.materiel_percent || 0) +
              (settings.loyer_percent || 0) +
              (settings.internet_percent || 0) +
              (settings.management_percent || 0) +
              (settings.other_overheads_percent || 0)
            ).toFixed(1)}% du salaire brut
          </p>
        </div>
      </Card>

      {/* Marges cibles */}
      <Card className="p-6 border-tp-border">
        <h3 className="text-lg font-semibold text-tp-text mb-4 flex items-center gap-2">
          <Percent className="h-5 w-5 text-tp-accent" />
          Marges cibles
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="margin_assistant">Marge cible Assistant Virtuel (%)</Label>
            <Input
              id="margin_assistant"
              type="number"
              step="0.1"
              value={settings.target_margin_assistant_percent || ''}
              onChange={(e) => updateSetting('target_margin_assistant_percent', e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-tp-text-light mt-1">
              Utilisé pour calculer le prix de vente recommandé des assistants
            </p>
          </div>
          <div>
            <Label htmlFor="margin_tech">Marge cible Talent Technique (%)</Label>
            <Input
              id="margin_tech"
              type="number"
              step="0.1"
              value={settings.target_margin_tech_percent || ''}
              onChange={(e) => updateSetting('target_margin_tech_percent', e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-tp-text-light mt-1">
              Utilisé pour calculer le prix de vente recommandé des profils tech
            </p>
          </div>
        </div>
      </Card>

      {/* Seuils d'alerte */}
      <Card className="p-6 border-tp-border">
        <h3 className="text-lg font-semibold text-tp-text mb-4 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-tp-warning" />
          Seuils d'alerte rentabilité
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="warning_margin" className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-orange-500"></span>
              Seuil Warning (%)
            </Label>
            <Input
              id="warning_margin"
              type="number"
              step="0.1"
              value={settings.warning_margin_percent || ''}
              onChange={(e) => updateSetting('warning_margin_percent', e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-tp-text-light mt-1">
              En dessous = rentabilité faible à surveiller
            </p>
          </div>
          <div>
            <Label htmlFor="danger_margin" className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              Seuil Danger (%)
            </Label>
            <Input
              id="danger_margin"
              type="number"
              step="0.1"
              value={settings.danger_margin_percent || ''}
              onChange={(e) => updateSetting('danger_margin_percent', e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-tp-text-light mt-1">
              En dessous = non rentable
            </p>
          </div>
        </div>
        
        <div className="mt-4 flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span>≥ {settings.warning_margin_percent}% = Rentable</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
            <span>{settings.danger_margin_percent}% - {settings.warning_margin_percent}% = À surveiller</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <span>&lt; {settings.danger_margin_percent}% = Non rentable</span>
          </div>
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-tp-primary hover:bg-tp-primary-light"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Sauvegarde...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Sauvegarder les paramètres
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default PricingSettingsSection;
